import { useAction, useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Id } from '../../convex/_generated/dataModel';
import type { ModelKey } from '../@types/posts';
import { usePostGenerationState } from './usePostGenerationState';

// =============================================================================
// Feature 014: Unified Multi-AI Post Generation Flow
// T038-T040: Orchestration hook for research + multi-model generation
// =============================================================================

/**
 * T038: usePostGenerationMultiAI Hook
 * Orchestrates the entire post generation flow for Feature 014:
 * 1. Research topic with Perplexity
 * 2. Generate posts from 3 AI models in parallel
 * 3. Update UI progressively as each model completes
 */
export function usePostGenerationMultiAI(postId: Id<'posts'> | undefined, topicId: Id<'topics'>) {
  const { state, dispatch } = usePostGenerationState(postId);
  const navigate = useNavigate();

  // Fetch business profile and AI preferences from unified businesses table
  const businessProfile = useQuery(api.businesses.queries.getMyBusinessProfile, {});
  const aiPreferences = useQuery(
    api.settings.aiPreferences.getAiModelPreferences,
    businessProfile ? { businessId: businessProfile._id } : 'skip'
  );

  // Extract selected models from preferences — only dispatch when actual selection changes
  const prevModelsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!aiPreferences) return;

    const selectedModels: ModelKey[] = [];
    if (aiPreferences.useClaude) selectedModels.push('claude');
    if (aiPreferences.useGemini) selectedModels.push('gemini');
    if (aiPreferences.useGPT) selectedModels.push('openai');

    const modelsKey = selectedModels.join(',');
    if (prevModelsKeyRef.current === modelsKey) return;
    prevModelsKeyRef.current = modelsKey;

    dispatch({ type: 'SET_SELECTED_MODELS', payload: { models: selectedModels } });
  }, [aiPreferences, dispatch]);

  // Actions
  const researchTopicAction = useAction(api.ai.postGeneration.researchTopic);
  const generateSingleModelPostAction = useAction(api.ai.postGeneration.generateSingleModelPost);
  const selectBestVariantAction = useMutation(api.posts.selectBestVariant);

  /**
   * T039: Handle Start Generation
   * Orchestrates research -> multi-model generation flow
   */
  const handleStartGeneration = useCallback(async () => {
    if (!postId) return;

    try {
      // Initialize post state
      dispatch({ type: 'INIT_POST', payload: { postId } });

      // Step 1: Research phase
      dispatch({ type: 'START_RESEARCH' });

      const researchResult = await researchTopicAction({
        postId,
        topicId,
      });

      dispatch({
        type: 'RESEARCH_SUCCESS',
        payload: {
          researchResults: researchResult.researchResults,
          cached: researchResult.cached,
        },
      });

      // Step 2: Start multi-model generation
      dispatch({ type: 'START_GENERATION' });

      // T040: Progressive dispatch logic - Generate only for selected models
      const modelPromises = state.selectedModels.map(async modelKey => {
        // Mark model as loading
        dispatch({ type: 'MODEL_GENERATION_START', payload: { modelKey } });

        try {
          const result = await generateSingleModelPostAction({
            postId,
            modelKey,
            researchResults: researchResult.researchResults,
            topicId,
          });

          // Check if generation failed (error field present)
          if (result.error) {
            // T040: Progressive UI update - dispatch error for this model
            dispatch({
              type: 'MODEL_GENERATION_ERROR',
              payload: {
                modelKey,
                error: result.error,
              },
            });
          } else {
            // T040: Progressive UI update - dispatch success as soon as this model completes
            dispatch({
              type: 'MODEL_GENERATION_SUCCESS',
              payload: {
                modelKey,
                postData: {
                  content: result.content,
                  model: result.model,
                  timestamp: result.timestamp,
                  cached: result.cached,
                },
              },
            });
          }
        } catch (error) {
          // T040: Progressive UI update - dispatch error for this model (unexpected exception)
          dispatch({
            type: 'MODEL_GENERATION_ERROR',
            payload: {
              modelKey,
              error: error instanceof Error ? error.message : 'Generation failed',
            },
          });
        }
      });

      // Wait for all models to complete (or fail)
      await Promise.allSettled(modelPromises);

      // Mark all models complete
      dispatch({ type: 'ALL_MODELS_COMPLETE' });
    } catch (error) {
      // Research phase error
      dispatch({
        type: 'RESEARCH_ERROR',
        payload: {
          error: error instanceof Error ? error.message : 'Research failed',
        },
      });
    }
  }, [
    postId,
    topicId,
    state.selectedModels,
    dispatch,
    researchTopicAction,
    generateSingleModelPostAction,
  ]);

  /**
   * Handle Select Best Variant
   * Saves user's choice and navigates to post editor
   */
  const handleSelectBestVariant = useCallback(
    async (modelKey: ModelKey) => {
      if (!postId) return;

      dispatch({ type: 'START_VARIANT_SELECTION', payload: { modelKey } });

      try {
        await selectBestVariantAction({
          postId,
          selectedModelKey: modelKey,
        });

        dispatch({ type: 'VARIANT_SELECTION_SUCCESS', payload: { modelKey } });

        // Redirect to post editor after successful selection
        navigate(`/post/${postId}`);
      } catch (error) {
        dispatch({
          type: 'VARIANT_SELECTION_ERROR',
          payload: {
            error: error instanceof Error ? error.message : 'Selection failed',
          },
        });
      }
    },
    [postId, selectBestVariantAction, dispatch, navigate]
  );

  return {
    state,
    handleStartGeneration,
    handleSelectBestVariant,
  };
}
